import { z } from "zod";
import { Id, Role, Timestamp } from "./common.ts";

/**
 * Admin action log ("Protokoll"): who changed what, when. ADMINS ONLY (moderators get 403 and
 * never see the page). Owner (2026-11-03): "who edited what dive spot when, who added or
 * suggested a new one and who accepted it".
 *
 * Source: the API's `audit_log` table. One row per entity per write, written in the SAME
 * transaction as the write. Already written for every staff write (spots, objects, object
 * models, lakes, users, settings, scripts with a system label). This contract adds:
 *
 * Schema (API migration):
 * - `spot_id uuid NULL`: the spot a row belongs to, SET AT WRITE TIME (writeAudit takes it) and
 *   backfilled once: a spot row → its id; object → its spot (objects never change spot and are
 *   soft-deleted); sighting → its object's spot; suggestion → the edited spot / the edited
 *   object's spot / the object_create payload's `spotId` / after approval of a spot_create its
 *   `resultId`. Null for lakes, users, settings and an unapproved spot_create. It is
 *   `target.spotId` and what the `spotId` filter matches (in SQL, before LIMIT and cursor).
 * - Indexes on (created_at DESC, id DESC) and (spot_id, created_at DESC, id DESC).
 *
 * New rows and their diffs (binding):
 * - submit (entity suggestion, actor = the diver, entityId = suggestion id):
 *   `{ kind, targetId }` only. Never the payload or the diver's free-text `note` (both stay in
 *   the suggestions table, where moderators read them).
 * - withdraw (suggestion, actor = the diver): `{ status: { from: "pending", to: "withdrawn" } }`.
 *   Deleting an account (DELETE /api/me) withdraws its pending suggestions with one withdraw row
 *   each, plus `reason: "account deleted"`, written before the user row goes.
 * - approve / reject (suggestion, actor = staff): `{ status, resultId, adjusted }` /
 *   `{ status, reason }` as written today. A migration renames the existing rows: entity
 *   suggestion, action update, `diff->'status'->>'to'` approved → approve, rejected → reject.
 * - sighting (entityId = sightings.id, actor = the diver): first report of the day `create`
 *   `{ objectId, seenOn, present }`; a same-day report that flips `present` (the unique row is
 *   updated) `update` `{ present: { from, to } }`; an unchanged repeat writes no row.
 * - user delete (DELETE /api/me, entityId = the user id): diff `{}`, written AFTER the
 *   redaction below (so it stays `{}`).
 * - Backfill (migration, idempotent: skip suggestions that already have a submit row): one
 *   submit row per existing suggestion at its `created_at`, actor_id = its submitter (null →
 *   "Gelöschtes Konto"), diff `{ kind, targetId, backfilled: true }`. Old withdrawals have no
 *   timestamp and are not backfilled.
 * - A spot or object created or edited by approving a suggestion keeps its own row (actor = the
 *   approving staff member) with `diff.suggestionId`; `suggestionId` below exposes it.
 * - Password changes: `{ password: { from: null, to: "changed" } }`; old rows hold the bare
 *   string `{ password: "changed" }`, which `auditChanges` reads the same way.
 *
 * Erasure (nFADP): rows are never edited or deleted by the app (no retention job, no admin
 * delete), with TWO exceptions: approving a spot_create sets `spot_id` (column only) on that
 * suggestion's earlier rows, so the diver's submit shows in the new spot's history; and
 * deleting an account (DELETE /api/me, divers only; staff cannot delete themselves) replaces,
 * in the same transaction, the diff of every row with entity user and that user's id by
 * `{ redacted: true }`. Their actor rows lose the link (actor_id → null: "Gelöschtes Konto");
 * suggestion and sighting diffs never held personal text, so nothing of the person stays
 * readable. A deleted user's `target.label` is null.
 *
 * Privacy: the log is as sensitive as the admin Users page (names, emails, ban state), so it is
 * admin-only, `Cache-Control: private, no-store`, and never cached by the service worker.
 * Diffs never contain passwords, tokens, session data or diver free text.
 */
export const AUDIT_ENTITIES = [
  "spot",
  "object",
  "suggestion",
  "sighting",
  "lake",
  "user",
  "settings",
] as const;
export const AuditEntity = z.enum(AUDIT_ENTITIES);
export type AuditEntity = z.infer<typeof AuditEntity>;

export const AUDIT_ACTIONS = [
  "create",
  "update",
  "delete",
  "submit",
  "withdraw",
  "approve",
  "reject",
] as const;
export const AuditAction = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof AuditAction>;

/**
 * Who did it.
 * - A user: `id`, current `name`, `email`, `role` (as they are NOW, not at the time).
 * - A script: `id: null`, `label` e.g. "system:bathy-sync"; name, email and role null.
 * - A deleted account: everything null.
 */
export const AuditActor = z
  .object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    role: Role.nullable(),
    label: z.string().nullable(),
  })
  .strict();
export type AuditActor = z.infer<typeof AuditActor>;

/**
 * What it was done to, resolved by the API for display and links.
 * - `label`: the current name (spot name; object/lake name via `pickLocale(…, "de")`; user
 *   name; suggestion: the proposed or edited spot/object name). For a deleted entity, the name
 *   from the row's diff (`before`/`after`) when there is one. Null when unknown (settings,
 *   hard-gone rows).
 * - `deleted`: the entity no longer exists or is soft-deleted (no link then, except a
 *   suggestion, which always links).
 * - `label` for a sighting: its object's name. A deleted (redacted) user: null.
 * - `spotId`: the row's stored `spot_id` (see above); the admin links to that spot.
 */
export const AuditTarget = z
  .object({
    label: z.string().nullable(),
    deleted: z.boolean(),
    spotId: Id.nullable(),
  })
  .strict();
export type AuditTarget = z.infer<typeof AuditTarget>;

export const AuditEntry = z
  .object({
    id: Id,
    at: Timestamp,
    actor: AuditActor,
    entity: AuditEntity,
    /** The entity's id (uuid, a user id, or "site" for settings). */
    entityId: z.string(),
    action: AuditAction,
    target: AuditTarget,
    /** The suggestion behind this write (approval rows of spots/objects; suggestion rows). */
    suggestionId: Id.nullable(),
    /**
     * The stored diff, as written:
     * - update: `{ field: { from, to } }` (see `auditChanges`), plus extras such as
     *   `suggestionId`;
     * - create: `{ after }` (sighting: `{ objectId, seenOn, present }`);
     * - delete: `{ before }` or `{ cascadedFromSpot }`;
     * - approve: `{ status, resultId, adjusted }`; reject: `{ status, reason }`.
     */
    diff: z.unknown(),
  })
  .strict();
export type AuditEntry = z.infer<typeof AuditEntry>;

/**
 * GET /api/admin/audit (admin). Newest first: ORDER BY created_at DESC, id DESC.
 * - `limit` default 50, max 200.
 * - `before`: the previous page's `next` cursor (opaque to the client). Rows written in one
 *   transaction share `created_at`, so the cursor is (created_at, id), never a timestamp alone,
 *   and it carries created_at at FULL (microsecond) precision, formatted in SQL
 *   (`to_char(created_at AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"')`) and compared in
 *   SQL, never round-tripped through a JS Date (ms), which would skip rows. Malformed → 400.
 * - Filters combine with AND, all applied in SQL before LIMIT: `entity`, `action`, `entityId`,
 *   `actorId` (matches actor_id, a user id, only; a system label matches nothing), `spotId`
 *   (the stored spot_id: a spot's whole history with its objects, sightings and suggestions).
 * - Unknown keys → 400.
 */
export const AuditListQuery = z
  .object({
    entity: AuditEntity.optional(),
    action: AuditAction.optional(),
    actorId: z.string().min(1).max(64).optional(),
    entityId: z.string().min(1).max(64).optional(),
    spotId: Id.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: z.string().min(1).max(200).optional(),
  })
  .strict();
export type AuditListQuery = z.infer<typeof AuditListQuery>;

export const AuditList = z
  .object({
    entries: z.array(AuditEntry),
    /** Cursor for the next (older) page; null when this page is the last. */
    next: z.string().nullable(),
  })
  .strict();
export type AuditList = z.infer<typeof AuditList>;

export type AuditChange = { field: string; from: unknown; to: unknown };

/** Diff keys that are context, not field changes. */
const CONTEXT_KEYS = new Set(["suggestionId", "cascadedFromSpot", "cascadedObjectIds"]);

/**
 * The field changes of an `update` row, in the diff's key order:
 * - a top-level key whose value is exactly `{ from, to }` → that field;
 * - one level of nesting (`model: { scale: { from, to } }`) → `model.scale`;
 * - the legacy bare string `password: "changed"` → `{ field: "password", from: null, to: "changed" }`.
 * Context keys, `after`, `before` and other shapes are not changes. Other actions → [].
 */
export function auditChanges(entry: Pick<AuditEntry, "action" | "diff">): AuditChange[] {
  if (entry.action !== "update") return [];
  const d = entry.diff;
  if (!isRecord(d)) return [];
  const out: AuditChange[] = [];
  for (const [field, v] of Object.entries(d)) {
    if (CONTEXT_KEYS.has(field)) continue;
    if (field === "password" && v === "changed") {
      out.push({ field, from: null, to: "changed" });
    } else if (isChange(v)) {
      out.push({ field, from: v.from, to: v.to });
    } else if (isRecord(v)) {
      for (const [sub, w] of Object.entries(v))
        if (isChange(w)) out.push({ field: `${field}.${sub}`, from: w.from, to: w.to });
    }
  }
  return out;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function isChange(v: unknown): v is { from: unknown; to: unknown } {
  return isRecord(v) && Object.keys(v).length === 2 && "from" in v && "to" in v;
}
